import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  TrendingUp,
  DollarSign,
  BarChart3,
  Star,
  ChevronDown,
  SlidersHorizontal,
  X,
  ShoppingCart,
} from 'lucide-react';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import MarketplaceCard from '../components/MarketplaceCard';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const categories = [
  { value: 'all', label: 'All Categories', icon: <BarChart3 className="w-4 h-4" /> },
  { value: 'trend-following', label: 'Trend Following', icon: <TrendingUp className="w-4 h-4" /> },
  { value: 'scalping', label: 'Scalping', icon: <DollarSign className="w-4 h-4" /> },
  { value: 'swing-trading', label: 'Swing Trading', icon: <BarChart3 className="w-4 h-4" /> },
  { value: 'support-resistance', label: 'Support & Resistance', icon: <Filter className="w-4 h-4" /> },
  { value: 'candle-patterns', label: 'Candle Patterns', icon: <BarChart3 className="w-4 h-4" /> },
  { value: 'chart-patterns', label: 'Chart Patterns', icon: <BarChart3 className="w-4 h-4" /> },
  { value: 'fvg', label: 'Fair Value Gap', icon: <BarChart3 className="w-4 h-4" /> },
];

const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'popular', label: 'Most Popular' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'profit', label: 'Highest Profit' },
];

const MarketplacePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [bots, setBots] = useState<any[]>([]);
  const [filteredBots, setFilteredBots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRunMode, setSelectedRunMode] = useState('all');
  const [selectedSort, setSelectedSort] = useState('newest');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [profitRange, setProfitRange] = useState({ min: '', max: '' });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    totalBots: 0,
    avgPrice: 0,
    avgRating: 0,
    totalPurchases: 0,
  });

  // Fetch marketplace bots
  const fetchBots = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('marketplace_bots')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setBots(data || []);

      // Calculate stats
      const totalBots = data?.length || 0;
      const avgPrice = data?.reduce((sum, bot) => sum + (bot.price_usd || 0), 0) / (totalBots || 1);
      const avgRating = data?.reduce((sum, bot) => sum + (bot.rating_avg || 0), 0) / (totalBots || 1);
      const totalPurchases = data?.reduce((sum, bot) => sum + (bot.purchases_count || 0), 0) || 0;

      setStats({ totalBots, avgPrice, avgRating, totalPurchases });
    } catch (error: any) {
      console.error('Error fetching marketplace bots:', error);
      toast.error('Failed to load marketplace bots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBots();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...bots];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (bot) =>
          bot.title?.toLowerCase().includes(query) ||
          bot.description?.toLowerCase().includes(query) ||
          bot.tags?.some((tag: string) => tag.toLowerCase().includes(query))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((bot) => bot.category === selectedCategory);
    }

    // Run mode filter
    if (selectedRunMode !== 'all') {
      filtered = filtered.filter(
        (bot) => bot.run_mode === selectedRunMode || bot.run_mode === 'both'
      );
    }

    // Price range filter
    if (priceRange.min !== '') {
      filtered = filtered.filter((bot) => bot.price_usd >= parseFloat(priceRange.min));
    }
    if (priceRange.max !== '') {
      filtered = filtered.filter((bot) => bot.price_usd <= parseFloat(priceRange.max));
    }

    // Profit range filter
    if (profitRange.min !== '') {
      filtered = filtered.filter(
        (bot) => (bot.performance_stats?.total_profit_pct || 0) >= parseFloat(profitRange.min)
      );
    }
    if (profitRange.max !== '') {
      filtered = filtered.filter(
        (bot) => (bot.performance_stats?.total_profit_pct || 0) <= parseFloat(profitRange.max)
      );
    }

    // Sort
    switch (selectedSort) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'popular':
        filtered.sort((a, b) => (b.purchases_count || 0) - (a.purchases_count || 0));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
        break;
      case 'price-low':
        filtered.sort((a, b) => a.price_usd - b.price_usd);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price_usd - a.price_usd);
        break;
      case 'profit':
        filtered.sort(
          (a, b) =>
            (b.performance_stats?.total_profit_pct || 0) -
            (a.performance_stats?.total_profit_pct || 0)
        );
        break;
    }

    setFilteredBots(filtered);
  }, [bots, searchQuery, selectedCategory, selectedRunMode, selectedSort, priceRange, profitRange]);

  const handleBuy = async (bot: any) => {
    if (!user) {
      toast.error('Please sign in to purchase bots');
      navigate('/signin');
      return;
    }

    // TODO: Implement payment flow
    toast.info('Purchase feature coming soon! This will integrate with payment processor.');

    // For now, simulate purchase
    console.log('Attempting to purchase bot:', bot.id);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedRunMode('all');
    setPriceRange({ min: '', max: '' });
    setProfitRange({ min: '', max: '' });
    setSelectedSort('newest');
  };

  return (
    <DashboardLayout>
      <main className="p-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-8"
        >
              {/* Hero Section */}
              <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-8 text-white">
                <div className="max-w-4xl">
                  <h1 className="text-4xl font-bold mb-3">Bot Marketplace</h1>
                  <p className="text-xl text-white/90 mb-6">
                    Discover and purchase profitable trading bots from expert traders
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold">{stats.totalBots}</div>
                      <div className="text-sm text-white/80">Available Bots</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold">${stats.avgPrice.toFixed(0)}</div>
                      <div className="text-sm text-white/80">Avg Price</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold">{stats.avgRating.toFixed(1)}</div>
                      <div className="text-sm text-white/80">Avg Rating</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold">{stats.totalPurchases}</div>
                      <div className="text-sm text-white/80">Total Sales</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search & Filters */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                {/* Search Bar */}
                <div className="relative mb-6">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search bots by name, description, or tags..."
                    className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  />
                </div>

                {/* Quick Filters */}
                <div className="flex flex-wrap gap-3 mb-4">
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => setSelectedCategory(cat.value)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                        selectedCategory === cat.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {cat.icon}
                      {cat.label}
                    </button>
                  ))}
                </div>

                {/* Advanced Filters Toggle */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                    {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
                  </button>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                    >
                      <X className="w-4 h-4" />
                      Clear All
                    </button>

                    <select
                      value={selectedSort}
                      onChange={(e) => setSelectedSort(e.target.value)}
                      className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      {sortOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Advanced Filters Panel */}
                {showAdvancedFilters && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Run Mode Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Trading Mode
                        </label>
                        <select
                          value={selectedRunMode}
                          onChange={(e) => setSelectedRunMode(e.target.value)}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="all">All Modes</option>
                          <option value="live">Live Trading</option>
                          <option value="paper">Paper Trading</option>
                          <option value="both">Both</option>
                        </select>
                      </div>

                      {/* Price Range */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Price Range (USD)
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={priceRange.min}
                            onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                            placeholder="Min"
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <input
                            type="number"
                            value={priceRange.max}
                            onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                            placeholder="Max"
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>

                      {/* Profit Range */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Profit Range (%)
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={profitRange.min}
                            onChange={(e) => setProfitRange({ ...profitRange, min: e.target.value })}
                            placeholder="Min"
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <input
                            type="number"
                            value={profitRange.max}
                            onChange={(e) => setProfitRange({ ...profitRange, max: e.target.value })}
                            placeholder="Max"
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Results Count */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {filteredBots.length} {filteredBots.length === 1 ? 'Bot' : 'Bots'} Found
                </h2>
              </div>

              {/* Bot Grid */}
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : filteredBots.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredBots.map((bot) => (
                    <MarketplaceCard key={bot.id} bot={bot} onBuy={handleBuy} />
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-12 text-center">
                  <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    No bots found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Try adjusting your filters or search query
                  </p>
                  <button
                    onClick={clearFilters}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
        </motion.div>
      </main>
    </DashboardLayout>
  );
};

export default MarketplacePage;
